import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  checkUsernameAvailable,
  completeGoogleProfile,
  fetchMe,
  getAuthToken,
  isLoggedIn,
  saveAuthToken,
} from '../api/auth'
import './RegisterPage.css'

const USERNAME_CHECK_MS = 400
const USERNAME_RE = /^[a-z0-9_]{3,30}$/

type UsernameStatus = 'idle' | 'checking' | 'available' | 'taken'

function normalizeUsername(raw: string): string {
  return raw.replace(/^@+/, '').toLowerCase().replace(/[^a-z0-9_]/g, '')
}

export function GoogleUsernamePage() {
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>('idle')
  const usernameCheckId = useRef(0)

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login', { replace: true })
      return
    }
    fetchMe()
      .then((user) => {
        if (!user.needsUsername) {
          navigate('/', { replace: true })
          return
        }
        if (user.displayName && user.displayName !== 'New user') {
          setDisplayName(user.displayName)
        }
      })
      .catch(() => {
        navigate('/login', { replace: true })
      })
  }, [navigate])

  useEffect(() => {
    if (!USERNAME_RE.test(username)) {
      setUsernameStatus('idle')
      return
    }
    const id = ++usernameCheckId.current
    setUsernameStatus('checking')
    const timer = window.setTimeout(async () => {
      try {
        const available = await checkUsernameAvailable(username)
        if (id !== usernameCheckId.current) return
        setUsernameStatus(available ? 'available' : 'taken')
      } catch {
        if (id !== usernameCheckId.current) return
        setUsernameStatus('idle')
      }
    }, USERNAME_CHECK_MS)
    return () => {
      usernameCheckId.current += 1
      window.clearTimeout(timer)
    }
  }, [username])

  async function handleSubmit() {
    setError('')
    const normalized = normalizeUsername(username)
    if (!USERNAME_RE.test(normalized)) {
      setError('Username must be 3–30 characters (letters, numbers, underscore).')
      return
    }
    if (usernameStatus === 'checking') {
      setError('Still checking username availability.')
      return
    }
    if (usernameStatus === 'taken') {
      setError('This username is already taken.')
      return
    }
    if (!getAuthToken()) {
      navigate('/login', { replace: true })
      return
    }

    setSubmitting(true)
    try {
      const data = await completeGoogleProfile({
        username: normalized,
        displayName: displayName.trim() || undefined,
      })
      saveAuthToken(data.access_token)
      navigate('/', { replace: true })
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const usernameTaken = usernameStatus === 'taken'
  const usernameChecking = usernameStatus === 'checking'
  const usernameHint =
    usernameStatus === 'taken'
      ? 'This username is already taken.'
      : usernameStatus === 'checking'
        ? 'Checking availability…'
        : usernameStatus === 'available'
          ? 'Username is available.'
          : 'Your username cannot be changed later.'
  const usernameHintClass =
    usernameStatus === 'taken'
      ? 'register-field-hint register-field-hint--taken'
      : usernameStatus === 'checking'
        ? 'register-field-hint register-field-hint--warn'
        : usernameStatus === 'available'
          ? 'register-field-hint register-field-hint--ok'
          : 'register-field-hint'

  const blocked =
    USERNAME_RE.test(username) && (usernameChecking || usernameTaken)

  return (
    <div className="register-root">
      <div className="register-grain" aria-hidden="true" />
      <div className="register-page">
        <main className="register-hero">
          <h1 className="register-headline" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
            Pick your username
          </h1>
          <p className="register-description">
            You signed in with Google. Choose a public username before you continue.
          </p>
          <div className="register-area">
            <div className="register-auth-form">
              <div className="register-email-area">
                <input
                  className={`register-email-input${error || usernameTaken ? ' error' : ''}`}
                  type="text"
                  placeholder="Username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(normalizeUsername(e.target.value))}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                />
                <p className={usernameHintClass}>{usernameHint}</p>
                <input
                  className="register-email-input"
                  type="text"
                  placeholder="Display name (optional)"
                  autoComplete="name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                />
                <button
                  type="button"
                  className="register-submit-btn"
                  onClick={handleSubmit}
                  disabled={submitting || blocked}
                >
                  {submitting
                    ? 'Saving…'
                    : usernameChecking
                      ? 'Checking username…'
                      : 'Continue'}
                </button>
                {error && <p className="register-error-msg">{error}</p>}
              </div>
            </div>
          </div>
          <p className="register-fine-print">
            <Link to="/login" className="register-auth-switch-link">
              Back to login
            </Link>
          </p>
        </main>
      </div>
    </div>
  )
}
